import React, { useEffect, useState, useCallback } from 'react';
import {
  CalendarClock, TrendingUp, Users, Send, Loader2, Eye, Play,
  CheckCircle, AlertTriangle, Clock, Mail, RefreshCcw, Home,
} from 'lucide-react';
import { Button } from '../ui/button';
import { api, friendlyError } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

/**
 * Admin panel for the Day 1 / Day 3 / Day 7 follow-up email scheduler.
 *
 * Sections:
 *   1. Scheduler status banner (enabled, cron hour, last run)
 *   2. KPI grid: registered today, published today, pending, conversion %
 *   3. Per-day counters: sent + skipped (already_published)
 *   4. Per-day preview list (next candidates) + dry-run + real run
 *   5. Template preview modal (Day 1 / Day 3 / Day 7)
 */
export default function FollowupPanel() {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previews, setPreviews] = useState({ 1: [], 3: [], 7: [] });
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [templateDay, setTemplateDay] = useState(null);
  const [templateHtml, setTemplateHtml] = useState('');
  const [templateSubject, setTemplateSubject] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p1, p3, p7] = await Promise.all([
        api.adminFollowupStats(),
        api.adminFollowupPreview(1, 5),
        api.adminFollowupPreview(3, 5),
        api.adminFollowupPreview(7, 5),
      ]);
      setStats(s);
      setPreviews({ 1: p1.candidates, 3: p3.candidates, 7: p7.candidates });
    } catch (e) {
      toast({ title: 'No pudimos cargar', description: friendlyError(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { reload(); }, [reload]);

  const onRun = async (dryRun, day = null) => {
    const label = dryRun ? 'Dry-run' : 'Envío real';
    if (!dryRun) {
      const ok = window.confirm(`¿Ejecutar envío real ahora${day ? ` para Día ${day}` : ''}?\nResend enviará los correos de inmediato.`);
      if (!ok) return;
    }
    setRunning(true);
    setLastRun(null);
    try {
      const res = await api.adminFollowupRun(dryRun, day, 500);
      setLastRun({ ...res, label });
      toast({ title: `${label} OK`, description: `${res.duration_ms} ms — revisa los detalles abajo` });
      await reload();
    } catch (e) {
      toast({ title: `${label} falló`, description: friendlyError(e), variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  const openTemplate = async (day) => {
    setTemplateDay(day);
    setTemplateHtml('');
    setTemplateSubject('Cargando…');
    try {
      const res = await api.adminFollowupTemplate(day, 'María González');
      setTemplateSubject(res.subject);
      setTemplateHtml(res.html);
    } catch (e) {
      toast({ title: 'No pudimos cargar la plantilla', description: friendlyError(e), variant: 'destructive' });
      setTemplateDay(null);
    }
  };

  if (loading && !stats) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center border border-gray-200" data-testid="followup-panel-loading">
        <Loader2 className="w-8 h-8 text-habita animate-spin mx-auto" />
        <p className="text-sm text-gray-500 mt-3">Cargando métricas de seguimiento…</p>
      </div>
    );
  }
  if (!stats) return null;

  const k = stats.kpis;
  const e = stats.emails_sent;
  const sk = stats.skipped_already_published;

  return (
    <div className="space-y-6" data-testid="followup-panel">
      {/* Banner */}
      <div className={`rounded-2xl border p-5 ${stats.enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center gap-3">
          {stats.enabled
            ? <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
            : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-habita-dark text-sm">
              Scheduler {stats.enabled ? 'activo' : 'desactivado (FOLLOWUP_ENABLED=false)'}
            </p>
            <p className="text-xs text-gray-600 mt-0.5">
              <Clock className="w-3 h-3 inline mr-1 -mt-0.5" />
              Corre diariamente a las {String(stats.cron_hour_colombia).padStart(2, '0')}:00 hora Colombia
              {stats.last_run && (
                <>
                  {' · '}Última ejecución:{' '}
                  <strong>{new Date(stats.last_run.started_at).toLocaleString('es-CO')}</strong>
                  {stats.last_run.dry_run && <span className="ml-1 text-amber-700">(dry-run)</span>}
                </>
              )}
            </p>
          </div>
          <button
            onClick={reload}
            data-testid="followup-refresh-btn"
            className="text-xs font-semibold px-3 h-8 rounded-full border border-gray-300 hover:bg-white flex items-center gap-1"
          >
            <RefreshCcw className="w-3 h-3" /> Refrescar
          </button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200">
        <h3 className="font-serif text-lg font-bold text-habita-dark flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-habita" /> KPIs de activación
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <Kpi label="Registrados hoy" value={k.registered_today} icon={Users} testid="kpi-registered-today" />
          <Kpi label="Publicadas hoy" value={k.properties_published_today} icon={Home} testid="kpi-published-today" />
          <Kpi label="Pendientes por publicar" value={k.users_pending_to_publish} accent="text-amber-600" testid="kpi-pending" />
          <Kpi
            label="Conversión a 1ª publicación"
            value={`${k.first_publication_conversion_pct}%`}
            accent="text-habita"
            testid="kpi-conversion"
          />
          <Kpi
            label="Usuarios totales"
            value={k.users_total}
            testid="kpi-users-total"
          />
          <Kpi
            label="Publicaron 1ª propiedad"
            value={k.users_published_first_property}
            accent="text-emerald-600"
            testid="kpi-published-first"
          />
          <Kpi
            label="Días promedio a publicar"
            value={k.avg_days_signup_to_first_publication ?? '—'}
            testid="kpi-avg-days"
          />
          <Kpi
            label="Opt-outs"
            value={k.optouts}
            accent={k.optouts > 0 ? 'text-rose-600' : ''}
            testid="kpi-optouts"
          />
        </div>
      </div>

      {/* Per-day card */}
      <div className="grid lg:grid-cols-3 gap-4">
        {[1, 3, 7].map(day => (
          <div key={day} className="bg-white rounded-2xl p-6 border border-gray-200" data-testid={`followup-day-card-${day}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-habita-mint text-habita flex items-center justify-center">
                  <CalendarClock className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-500">Recordatorio</p>
                  <p className="font-serif font-bold text-habita-dark">Día {day}</p>
                </div>
              </div>
              <button
                onClick={() => openTemplate(day)}
                className="text-xs font-semibold px-3 h-8 rounded-full border border-gray-200 hover:border-habita flex items-center gap-1"
                data-testid={`followup-template-btn-${day}`}
              >
                <Eye className="w-3 h-3" /> Vista previa
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-4">
              <div className="bg-habita-cream rounded-xl p-3">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Enviados</p>
                <p className="text-2xl font-bold mt-1 text-emerald-700" data-testid={`followup-sent-${day}`}>{e[`day${day}`]}</p>
              </div>
              <div className="bg-habita-cream rounded-xl p-3">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Saltados (ya publicó)</p>
                <p className="text-2xl font-bold mt-1 text-habita" data-testid={`followup-skipped-${day}`}>{sk[`day${day}`]}</p>
              </div>
            </div>

            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Próximos candidatos</p>
              {previews[day]?.length === 0 ? (
                <p className="text-xs text-gray-400 italic">Sin candidatos en este momento.</p>
              ) : (
                <div className="space-y-1 max-h-32 overflow-y-auto text-xs">
                  {previews[day].map(c => (
                    <div key={c.user_id} className="flex items-center gap-1.5 truncate">
                      <span className="text-gray-400">·</span>
                      <span className="font-mono truncate flex-1">{c.email}</span>
                      {c.will_be_skipped && (
                        <span className="text-[9px] text-gray-400 uppercase">skip</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-4">
              <Button
                onClick={() => onRun(true, day)}
                disabled={running}
                variant="outline"
                size="sm"
                data-testid={`followup-dryrun-${day}`}
                className="flex-1 h-9 rounded-full text-xs"
              >
                {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Eye className="w-3 h-3 mr-1" /> Dry-run</>}
              </Button>
              <Button
                onClick={() => onRun(false, day)}
                disabled={running}
                size="sm"
                data-testid={`followup-run-${day}`}
                className="btn-habita flex-1 h-9 rounded-full text-xs"
              >
                {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3 mr-1" /> Enviar</>}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Global run */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-serif text-lg font-bold text-habita-dark flex items-center gap-2">
              <Play className="w-4 h-4 text-habita" /> Ejecutar todos los días ahora
            </h3>
            <p className="text-xs text-gray-500 mt-1">Útil para QA o si quieres adelantar la corrida diaria.</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => onRun(true)}
              disabled={running}
              variant="outline"
              data-testid="followup-dryrun-all"
              className="h-11 px-5 rounded-full"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Eye className="w-4 h-4 mr-1.5" /> Dry-run (todos)</>}
            </Button>
            <Button
              onClick={() => onRun(false)}
              disabled={running}
              data-testid="followup-run-all"
              className="btn-habita h-11 px-5 rounded-full"
            >
              {running
                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Enviando...</>
                : <><Send className="w-4 h-4 mr-1.5" /> Ejecutar real</>}
            </Button>
          </div>
        </div>

        {lastRun && (
          <div className="mt-5 p-4 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="followup-last-run">
            <p className="text-sm font-semibold text-habita-dark mb-2 flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              {lastRun.label} · {lastRun.duration_ms}ms
              {lastRun.dry_run && <span className="text-amber-700 text-xs">(no se enviaron correos)</span>}
            </p>
            <div className="grid sm:grid-cols-3 gap-2 text-xs">
              {Object.entries(lastRun.per_day || {}).map(([d, r]) => (
                <div key={d} className="bg-white rounded-lg p-2">
                  <p className="font-bold text-habita-dark">Día {d}</p>
                  <p className="text-emerald-700">✉️ Enviados: {r.sent_ok}</p>
                  <p className="text-habita">⏭ Saltados (publicó): {r.skipped_already_published}</p>
                  {r.failed_count > 0 && <p className="text-rose-700">✗ Fallidos: {r.failed_count}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Template preview modal */}
      {templateDay && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setTemplateDay(null)}
          data-testid="followup-template-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-habita" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Plantilla Día {templateDay}</p>
                  <p className="font-semibold text-habita-dark text-sm">{templateSubject}</p>
                </div>
              </div>
              <button
                onClick={() => setTemplateDay(null)}
                data-testid="followup-template-close"
                className="text-gray-500 hover:text-habita-dark text-xl px-2"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-habita-cream">
              <iframe
                title={`Plantilla Día ${templateDay}`}
                srcDoc={templateHtml}
                className="w-full h-[600px] bg-white"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="bg-habita-cream rounded-xl p-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
        {Icon && <Icon className="w-3 h-3 text-gray-400" />}
      </div>
      <p className={`text-2xl font-bold mt-1 ${accent || 'text-habita-dark'}`}>{value}</p>
    </div>
  );
}
